import React, { useEffect, useState, useMemo } from "react";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  Typography,
  Tooltip,
  Paper,
  Button,
  Popover,
} from "@mui/material";

import { MdVerified, MdOutlineError } from "react-icons/md";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { candidateProfilev2 } from "../../../Redux/action/CandidateProfileAction";
import Loader from "../../Common/Loader/loader";
import BackButton from "../../Common/BackButton/BackButton";
import dummyDp from "../../../../src/images/userimg.jpeg";
import { Verified } from "@mui/icons-material";

/* ---------------- Project Carousel ---------------- */
const ProjectCarousel = ({ images = [] }) => {
  if (!images.length) return null;

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: images.length > 1 ? 2 : 1,
    responsive: [{ breakpoint: 900, settings: { slidesToShow: 1 } }],
  };

  return (
    <Slider {...settings}>
      {images.map((img, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{ p: 1, display: "flex", justifyContent: "center" }}
        >
          <img
            src={img}
            alt="project"
            loading="lazy"
            style={{
              height: 180,
              width: "90%",
              borderRadius: 8,
              objectFit: "cover",
            }}
          />
        </Paper>
      ))}
    </Slider>
  );
};

/* ================= MAIN ================= */

const AppliedCandidateView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { result: userInfo, loading } = useSelector((s) => s.Profile);
  const [priceAnchor, setPriceAnchor] = useState(null);
  const [pricingData, setPricingData] = useState({});

  /* ---------------- Candidate ID ---------------- */
  const candidateId = useMemo(() => {
    const qp = new URLSearchParams(location.search).get("candidateId");
    return qp || params.id;
  }, [location.search, params.id]);

  /* ---------------- Fetch ---------------- */
  useEffect(() => {
    if (candidateId) {
      dispatch(candidateProfilev2(candidateId));

      axios
        .get(
          `${process.env.REACT_APP_API_PATH}/ebench/candidate/${candidateId}/price`
        )
        .then((res) => setPricingData(res?.data?.data || {}))
        .catch(() => {});
    }
  }, [candidateId, dispatch]);

  /* ---------------- Experience ---------------- */
  const experienceText = useMemo(() => {
    const y = parseInt(userInfo?.keyExperience || 0);
    const m = parseInt(userInfo?.keyExperienceInMonth || 0);
    if (!y && !m) return "Fresher";
    if (!m) return `${y} Years`;
    if (!y) return `${m} Months`;
    return `${y} Years ${m} Months`;
  }, [userInfo]);

  /* ---------------- Loading ---------------- */
  if (loading) {
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader />
      </Box>
    );
  }

  const PriceRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
        borderBottom: "1px dashed #CBD5E1",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography sx={{ fontSize: 14, color: "#475569" }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, color: "#286a94" }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Box sx={{ px: 3, pb: 4 }}>
        <Grid container spacing={3}>
          {/* ================= LEFT PANEL ================= */}
          <Grid item xs={12} md={4} lg={3}>
            <Box sx={{ position: "sticky", top: 20 }}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background:
                    "linear-gradient(180deg, #F4F9FD 0%, #FFFFFF 65%)",
                  boxShadow: "0 10px 30px rgba(40,106,148,0.12)",
                  transition: "0.3s",
                  "&:hover": {
                    boxShadow: "0 14px 38px rgba(40,106,148,0.18)",
                  },
                }}
              >
                {/* Back Button */}
                <Box sx={{ mb: 1 }}>
                  <BackButton />
                </Box>

                {/* Avatar + Status */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: "5px",
                      borderRadius: "50%",
                    }}
                  >
                    <Avatar
                      src={dummyDp}
                      sx={{
                        width: 160,
                        height: 160,
                        border: "4px solid #fff",
                      }}
                      imgProps={{ loading: "eager", decoding: "async" }}
                    />
                  </Box>
                </Box>

                {/* Name */}
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ textAlign: "center" }}
                >
                  {userInfo?.firstName} {userInfo?.lastName}
                </Typography>

                {/* Status Chip */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Chip
                    icon={
                      userInfo?.emailVerified ? (
                        <MdVerified size={18} color="#286a94" />
                      ) : (
                        <MdOutlineError size={18} color="#B91C1C" />
                      )
                    }
                    label={userInfo?.emailVerified ? "Verified" : "Unverified"}
                    sx={{
                      fontWeight: 600,
                      px: 1,
                      background: userInfo?.emailVerified
                        ? "#E6F0F8"
                        : "#FEE2E2",
                      color: userInfo?.emailVerified ? "#286a94" : "#B91C1C",
                      border: "1px solid",
                      borderColor: userInfo?.emailVerified
                        ? "#B6D4EA"
                        : "#FCA5A5",
                      "& .MuiChip-icon": {
                        ml: "6px",
                      },
                    }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Contact Information */}
                {/* <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "#286a94",
                      mb: 1.5,
                    }}
                  >
                    Contact Information
                  </Typography>

                  <Box sx={{ display: "grid", gap: 1.2 }}>
                    {userInfo?.email && (
                      <Tooltip title={userInfo.email}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.4,
                            p: 1.2,
                            borderRadius: 2,
                            background: "#F8FAFC",
                            border: "1px solid #E2E8F0",
                            cursor: "pointer",
                            transition: "all 0.25s ease",
                            "&:hover": {
                              background: "#E6F0F8",
                              borderColor: "#B6D4EA",
                              transform: "translateY(-1px)",
                            },
                          }}
                          onClick={() =>
                            window.open(`mailto:${userInfo.email}`)
                          }
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: "#E6F0F8",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <MdOutlineMail size={18} color="#286a94" />
                          </Box>

                          <Box sx={{ flexGrow: 1 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                              Email
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                              {userInfo.email.length > 30
                                ? userInfo.email.slice(0, 30) + "..."
                                : userInfo.email}
                            </Typography>
                          </Box>

                          <Typography sx={{ color: "#94A3B8", fontSize: 18 }}>
                            →
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                    {userInfo?.mobile && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.4,
                          p: 1.2,
                          borderRadius: 2,
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: "#E6F0F8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <MdOutlineLocalPhone size={18} color="#286a94" />
                        </Box>

                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            Phone
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            +{userInfo?.dialCode} {userInfo.mobile}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {userInfo?.linkedIn && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.4,
                          p: 1.2,
                          borderRadius: 2,
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          cursor: "pointer",
                          transition: "all 0.25s ease",
                          "&:hover": {
                            background: "#E6F0F8",
                            borderColor: "#B6D4EA",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => window.open(userInfo.linkedIn, "_blank")}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: "#E6F0F8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <AiOutlineLinkedin size={18} color="#286a94" />
                        </Box>

                        <Box sx={{ flexGrow: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            LinkedIn
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            View profile
                          </Typography>
                        </Box>

                        <Typography sx={{ color: "#94A3B8", fontSize: 18 }}>
                          →
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box> */}
              </Card>
            </Box>
          </Grid>

          {/* ================= RIGHT PANEL ================= */}
          <Grid item xs={12} md={8} lg={9}>
            <Card
              sx={{
                p: 4,
                borderRadius: 4,
                boxShadow: "0px 10px 30px rgba(40,106,148,0.08)",
                background: "#FFFFFF",
              }}
            >
              {/* ================= HEADER ================= */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  {/* Designation + Verified */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "#0f172a",
                        lineHeight: 1.3,
                      }}
                    >
                      {userInfo?.currentDesignation || "N/A"}
                    </Typography>

                    {userInfo?.emailVerified && (
                      <Tooltip title="Verified profile">
                        <Verified
                          sx={{
                            color: "#286a94",
                            fontSize: 22,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Candidate ID */}
                  <Box
                    sx={{
                      mt: 0.8,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.8,
                      px: 1.5,
                      py: 0.4,
                      borderRadius: 999,
                      background: "#F1F7FB",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#64748b",
                        fontWeight: 500,
                        textTransform: "uppercase",
                      }}
                    >
                      Candidate ID :
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#286a94",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {userInfo?.candidateEmpId || "--"}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {/* PRICE */}
                  {(pricingData?.perHour ||
                    pricingData?.perDay ||
                    pricingData?.perMonth) && (
                    <>
                      <Button
                        onClick={(e) => setPriceAnchor(e.currentTarget)}
                        startIcon={<PaidOutlinedIcon />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "999px",
                          px: 2.5,
                          height: 38,
                          border: "1px solid #B6D4EA",
                          color: "#286a94",
                          background: "#E6F0F8",
                          "&:hover": {
                            background: "#D8ECFB",
                            borderColor: "#286a94",
                          },
                        }}
                      >
                        Price
                      </Button>

                      <Popover
                        open={Boolean(priceAnchor)}
                        anchorEl={priceAnchor}
                        onClose={() => setPriceAnchor(null)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <Paper
                          sx={{
                            p: 2,
                            minWidth: 230,
                            borderRadius: 2,
                            border: "1px solid #B6D4EA",
                            boxShadow: "0px 10px 30px rgba(40,106,148,0.15)",
                          }}
                        >
                          <Typography
                            fontWeight={700}
                            sx={{ mb: 1, color: "#286a94" }}
                          >
                            Pricing
                          </Typography>

                          {pricingData?.perHour && (
                            <PriceRow
                              label="Per Hour"
                              value={`₹${pricingData.perHour}`}
                            />
                          )}
                          {pricingData?.perDay && (
                            <PriceRow
                              label="Per Day"
                              value={`₹${pricingData.perDay}`}
                            />
                          )}
                          {pricingData?.perMonth && (
                            <PriceRow
                              label="Per Month"
                              value={`₹${pricingData.perMonth}`}
                            />
                          )}
                        </Paper>
                      </Popover>
                    </>
                  )}

                  {/* RESUME */}
                  {/* {userInfo?.resumeFileUrl && (
                    <Button
                      component="a"
                      href={userInfo.resumeFileUrl}
                      target="_blank"
                      startIcon={<VisibilityOutlinedIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "999px",
                        px: 2.5,
                        height: 38,
                        border: "1px solid #CBD5E1",
                        color: "#334155",
                        background: "#F8FAFC",
                        "&:hover": {
                          background: "#F1F5F9",
                        },
                      }}
                    >
                      Resume
                    </Button>
                  )} */}

                  {/* INTRODUCTION */}
                  {/* {userVideo && (
                    <Button
                      onClick={() => setShowVideoModal(true)}
                      startIcon={<PlayCircleOutlineIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "999px",
                        px: 2.8,
                        height: 38,
                        background: "#286a94",
                        color: "#ffffff",
                        boxShadow: "0 6px 18px rgba(40,106,148,0.35)",
                        "&:hover": {
                          background: "#215b7f",
                        },
                      }}
                    >
                      Introduction
                    </Button>
                  )} */}
                </Box>
              </Box>

              {/* ================= OVERVIEW ================= */}
              {userInfo?.overview && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background: "#F8FAFC",
                    borderLeft: "4px solid #286a94",
                  }}
                >
                  {/* Label */}
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#286a94",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 0.75,
                    }}
                  >
                    Professional Summary
                  </Typography>

                  {/* Content */}
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#475569",
                      lineHeight: 1.7,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {userInfo.overview}
                  </Typography>
                </Box>
              )}

              {/* {userInfo?.overview && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background: "#F8FAFC",
                    borderLeft: "4px solid #286a94",
                  }}
                >
                  <Typography sx={{ color: "#475569", lineHeight: 1.7 }}>
                    {userInfo.overview}
                  </Typography>
                </Box>
              )} */}

              <Divider sx={{ my: 4 }} />
              {/* ================= LOCATION & EXPERIENCE ================= */}
              <Grid container spacing={2}>
                {/* Location */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: "#F1F7FB",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        background: "#E6F0F8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <LocationOnIcon sx={{ color: "#286a94" }} />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#286a94",
                          textTransform: "uppercase",
                        }}
                      >
                        Location
                      </Typography>
                      <Typography sx={{ color: "#475569", fontSize: 14 }}>
                        {userInfo?.city || "—"}
                        {userInfo?.state ? `, ${userInfo.state}` : ""}
                        {userInfo?.country ? `, ${userInfo.country}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Experience */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: "#F1F7FB",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        background: "#E6F0F8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <WorkHistoryIcon sx={{ color: "#286a94" }} />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#286a94",
                          textTransform: "uppercase",
                        }}
                      >
                        Experience
                      </Typography>
                      <Typography sx={{ color: "#475569", fontSize: 14 }}>
                        {experienceText}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* ================= SKILLS ================= */}
              <Box sx={{ mt: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BuildOutlinedIcon
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#286a94">
                      Skills
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                      Technical & professional expertise
                    </Typography>
                  </Box>
                </Box>

                {/* Skills Container */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 3,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {userInfo?.skills?.length > 0 ? (
                      userInfo?.skills?.map((skill, i) => (
                        <Chip
                          key={i}
                          label={skill}
                          sx={{
                            fontWeight: 600,
                            fontSize: 13,
                            background: "#E6F0F8",
                            color: "#286a94",
                            border: "1px solid #B6D4EA",
                            px: 1,
                            "&:hover": {
                              background: "#D8ECFB",
                            },
                          }}
                        />
                      ))
                    ) : (
                      <Box
                        sx={{
                          p: 3,
                          width: "100%",
                          textAlign: "center",
                          border: "2px dashed #B6D4EA",
                          borderRadius: 2,
                          color: "#64748b",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          No skills added
                        </Typography>
                        <Typography sx={{ fontSize: 13 }}>
                          Skills will appear here once added
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* ================= PROJECTS ================= */}
              <Box sx={{ mt: 5 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AssignmentOutlinedIcon
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#286a94">
                    Projects
                  </Typography>
                </Box>

                {userInfo?.candidateProjects?.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {userInfo.candidateProjects.map((proj, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          mb: 3,
                          p: 3,
                          borderRadius: 3,
                          background: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                          transition: "0.25s",
                          "&:hover": {
                            background: "#F1F8FF",
                            borderColor: "#B6D4EA",
                          },
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
                            {proj.projectName || "Untitled Project"}
                          </Typography>

                          {proj.duration && (
                            <Chip
                              size="small"
                              icon={
                                <AccessTimeIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "#286a94 !important",
                                  }}
                                />
                              }
                              label={`Duration: ${proj.duration}`}
                              sx={{
                                background: "#E6F0F8",
                                color: "#286a94",
                                fontWeight: 600,
                                border: "1px solid #B6D4EA",
                                "& .MuiChip-icon": {
                                  ml: "6px",
                                },
                              }}
                            />
                          )}
                        </Box>

                        {/* Meta Grid */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#286a94",
                                textTransform: "uppercase",
                              }}
                            >
                              Role
                            </Typography>
                            <Typography sx={{ color: "#475569" }}>
                              {proj.role || "N/A"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#286a94",
                                textTransform: "uppercase",
                              }}
                            >
                              Technology Used
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              {proj?.technologies?.length > 0 ? (
                                proj.technologies.map((t, i) => (
                                  <Chip
                                    key={i}
                                    size="small"
                                    label={t}
                                    sx={{
                                      background: "#E6F0F8",
                                      color: "#286a94",
                                      border: "1px solid #B6D4EA",
                                      fontWeight: 600,
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography sx={{ color: "#64748b" }}>
                                  --
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Description */}
                        {proj.description && (
                          <Box
                            sx={{
                              mt: 2.5,
                              p: 2.5,
                              borderRadius: 2,
                              background: "#FFFFFF",
                              borderLeft: "3px solid #286a94",
                            }}
                          >
                            {/* Label */}
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#286a94",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                mb: 0.75,
                              }}
                            >
                              Project Description
                            </Typography>

                            {/* Description */}
                            <Typography
                              sx={{
                                fontSize: 14,
                                color: "#475569",
                                whiteSpace: "pre-line",
                                lineHeight: 1.65,
                              }}
                            >
                              {proj.description}
                            </Typography>
                          </Box>
                        )}

                        {/* Project URL */}
                        {proj?.projectUrl && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#286a94",
                                textTransform: "uppercase",
                              }}
                            >
                              Project URL
                            </Typography>

                            <Typography
                              component="a"
                              href={
                                proj.projectUrl.startsWith("http")
                                  ? proj.projectUrl
                                  : `https://${proj.projectUrl}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontSize: 14,
                                color: "#286a94",
                                fontWeight: 600,
                                wordBreak: "break-word",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {proj.projectUrl}
                            </Typography>
                          </Box>
                        )}

                        {/* Screenshots */}
                        {proj?.projectImages?.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#286a94",
                                textTransform: "uppercase",
                                mb: 1,
                              }}
                            >
                              Screenshots
                            </Typography>
                            <ProjectCarousel images={proj.projectImages} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #B6D4EA",
                      borderRadius: 3,
                      background: "#F8FCFF",
                      color: "#475569",
                    }}
                  >
                    📁 No projects added yet.
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* ================= EDUCATION ================= */}
              <Box sx={{ mt: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SchoolOutlined
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#286a94">
                    Education
                  </Typography>
                </Box>
                <Divider sx={{ my: 3 }} />

                {(!userInfo?.educations ||
                  userInfo?.educations?.length === 0) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                      color: "#475569",
                    }}
                  >
                    🎓 No education records found.
                  </Box>
                )}

                {userInfo?.educations?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {userInfo?.educations?.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          mb: 2.5,
                          background: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                          "&:hover": { background: "#f1f8ff" },
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Chip
                            label={`#${idx + 1}`}
                            sx={{
                              background: "#286a94",
                              color: "white",
                              mr: 2,
                              fontWeight: 600,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "18px", fontWeight: 600 }}
                          >
                            {item?.degreeName || item?.type || "Education"}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              University / Board
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.institutionName ||
                                item?.board ||
                                item?.university ||
                                "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              Passing Year
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.educationStatus === "PURSUING"
                                ? "Pursuing"
                                : item?.passingYear || "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              Grade / Percentage
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.percentage ? `${item.percentage}%` : "--"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* ================= CERTIFICATIONS ================= */}
              <Box sx={{ mt: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <WorkspacePremiumOutlinedIcon
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#286a94">
                    Certifications
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {(!userInfo?.candidateCertificate ||
                  userInfo?.candidateCertificate.length === 0) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                      color: "#475569",
                    }}
                  >
                    🎓 No certifications added yet.
                  </Box>
                )}

                {userInfo?.candidateCertificate?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {userInfo.candidateCertificate.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          background: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                          p: 2.5,
                          borderRadius: 3,
                          mb: 2.5,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Chip
                            label={`#${idx + 1}`}
                            sx={{
                              background: "#286a94",
                              color: "white",
                              mr: 2,
                              fontWeight: 600,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "18px", fontWeight: 600 }}
                          >
                            {item?.courseName || "Untitled Certification"}
                          </Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#0E3F6F",
                              }}
                            >
                              Issued By
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.courseProvider || "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#0E3F6F",
                              }}
                            >
                              Duration
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.duration || "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#0E3F6F",
                              }}
                            >
                              Year
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.year || "--"}
                            </Typography>
                          </Grid>

                          {item?.certificateImageUrl && (
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  color: "#0E3F6F",
                                }}
                              >
                                Certificate
                              </Typography>
                              <Typography
                                component="a"
                                href={item.certificateImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  fontSize: "15px",
                                  color: "#2563eb",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 1,
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                📄 View
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* VIDEO MODAL */}
      {/* <Suspense fallback={<CircularProgress />}>
        <VideoModal
          showModal={showVideoModal}
          handleClose={() => setShowVideoModal(false)}
          title="Introduction"
          body={
            userVideo ? (
              <video width="100%" height="300px" controls>
                <source src={userVideo} type="video/mp4" />
              </video>
            ) : (
              <img
                src={videoThumb}
                alt="No Video"
                style={{ width: "100%", maxHeight: 300 }}
              />
            )
          }
        />
      </Suspense> */}
    </>
  );
};

export default AppliedCandidateView;
